import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';
import { TOKEN_KEY, REFRESH_TOKEN_KEY, PENDING_CART_ITEM_KEY } from '../constants/storageKeys';
import './Login.css';

// Floating orb component
const FloatingOrb = ({ delay, duration, size, color, top, left }) => (
  <motion.div
    className="floating-orb"
    style={{
      width: size,
      height: size,
      background: color,
      top,
      left,
    }}
    animate={{
      y: [0, -30, 0],
      x: [0, 15, 0],
      scale: [1, 1.1, 1],
      opacity: [0.3, 0.6, 0.3],
    }}
    transition={{
      duration,
      delay,
      repeat: Infinity,
      ease: "easeInOut",
    }}
  />
);

// Animated particle
const Particle = ({ delay }) => (
  <motion.div
    className="particle"
    initial={{ opacity: 0, y: 100 }}
    animate={{
      opacity: [0, 1, 0],
      y: [-20, -200],
      x: [0, Math.random() * 60 - 30],
    }}
    transition={{
      duration: 4,
      delay,
      repeat: Infinity,
      ease: "easeOut",
    }}
  />
);

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const googleButtonRef = useRef(null);
  const googleInitialized = useRef(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Initialize Google Sign-In
  useEffect(() => {
    const initGoogleSignIn = async () => {
      if (googleInitialized.current) return;

      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      if (!clientId) {
        setError('Google Client ID not configured. Please set VITE_GOOGLE_CLIENT_ID in .env file');
        return;
      }

      try {
        await authService.initGoogleSignIn();

        if (window.google?.accounts) {
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: handleGoogleSignIn,
          });

          if (googleButtonRef.current && !googleInitialized.current) {
            window.google.accounts.id.renderButton(
              googleButtonRef.current,
              {
                theme: 'outline',
                size: 'large',
                width: '100%',
                text: 'signin_with',
                locale: 'en',
              }
            );
            googleInitialized.current = true;
          }
        }
      } catch (err) {
        console.error('Failed to initialize Google Sign-In:', err);
        setError('Failed to load Google Sign-In. Please refresh the page.');
      }
    };

    initGoogleSignIn();
  }, []);

  const handleGoogleSignIn = async (response) => {
    setLoading(true);
    setError('');

    try {
      const result = await authService.signInWithGoogle(response.credential);

      if (!result || !result.user) {
        throw new Error('Invalid response from server: user data missing');
      }

      if (!result.tokens || !result.tokens.accessToken) {
        throw new Error('Invalid response from server: access token missing');
      }

      const userData = {
        id: result.user.id,
        name: result.user.name || result.user.displayName || result.user.email?.split('@')[0],
        email: result.user.email,
        avatarUrl: result.user.avatarUrl,
        role: result.user.role,
      };

      localStorage.setItem(TOKEN_KEY, result.tokens.accessToken);
      if (result.tokens.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, result.tokens.refreshToken);
      }

      login(userData, result.tokens.accessToken);

      const pendingItem = sessionStorage.getItem(PENDING_CART_ITEM_KEY);
      if (pendingItem) {
        try {
          JSON.parse(pendingItem);
          sessionStorage.removeItem(PENDING_CART_ITEM_KEY);
        } catch (e) {
          console.error('Error parsing pending cart item:', e);
        }
      }

      const from = location.state?.from?.pathname || '/checkout';
      navigate(from, { replace: true });
    } catch (err) {
      console.error('Google sign-in error:', err);
      const errorMessage = err.response?.data?.message ||
                          err.response?.data?.error?.message ||
                          err.message ||
                          'Authentication failed. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { text: 'Custom Designs' },
    { text: '50+ Colors' },
    { text: 'Fast Delivery' },
    { text: 'Premium Quality' },
  ];

  return (
    <div className="login-page">
      {/* Animated Background */}
      <div className="login-background">
        <div className="aurora aurora-1"></div>
        <div className="aurora aurora-2"></div>
        <div className="aurora aurora-3"></div>

        {/* Floating Orbs */}
        <FloatingOrb delay={0} duration={6} size="300px" color="radial-gradient(circle, rgba(251, 191, 36, 0.15) 0%, transparent 70%)" top="10%" left="5%" />
        <FloatingOrb delay={1} duration={8} size="400px" color="radial-gradient(circle, rgba(244, 63, 94, 0.12) 0%, transparent 70%)" top="60%" left="70%" />
        <FloatingOrb delay={2} duration={7} size="250px" color="radial-gradient(circle, rgba(139, 92, 246, 0.12) 0%, transparent 70%)" top="30%" left="80%" />
        <FloatingOrb delay={0.5} duration={9} size="350px" color="radial-gradient(circle, rgba(251, 191, 36, 0.1) 0%, transparent 70%)" top="70%" left="10%" />

        {/* Grid Pattern */}
        <div className="grid-pattern"></div>

        {/* Particles */}
        <div className="particles-container">
          {[...Array(20)].map((_, i) => (
            <Particle key={i} delay={i * 0.3} />
          ))}
        </div>
      </div>

      <motion.div
        className="login-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        {/* Left Side - Login Card */}
        <motion.div
          className="login-card"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="login-card-glow"></div>

          <div className="login-header">
            <motion.div
              className="login-icon"
              animate={{
                rotate: [0, 10, -10, 0],
                scale: [1, 1.05, 1],
              }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              💡
            </motion.div>
            <h1 className="login-title">
              Welcome <span className="gradient-text">Back</span>
            </h1>
            <p className="login-subtitle">
              Sign in to continue designing your perfect lamp
            </p>
          </div>

          {error && (
            <motion.div
              className="error-message"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="error-icon">⚠️</span>
              {error}
            </motion.div>
          )}

          <div className="oauth-buttons">
            {loading && (
              <div className="loading-overlay">
                <div className="spinner"></div>
                <p>Signing in with Google...</p>
              </div>
            )}
            <div
              ref={googleButtonRef}
              className="google-signin-button-container"
              style={{
                opacity: loading ? 0.5 : 1,
                pointerEvents: loading ? 'none' : 'auto'
              }}
            />
          </div>

          <div className="login-footer">
            <p className="login-note">
              By continuing, you agree to our <a href="#">Terms of Service</a> and <a href="#">Privacy Policy</a>
            </p>
          </div>
        </motion.div>

        {/* Right Side - Illustration */}
        <motion.div
          className="login-illustration"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="illustration-content">
            {/* Animated Lamp */}
            <div className="lamp-showcase">
              <div className="lamp-glow"></div>
              <motion.div
                className="lamp-illustration"
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="lamp-shade-ill">
                  <div className="lamp-shade-shine"></div>
                </div>
                <div className="lamp-rod"></div>
                <div className="lamp-base-ill">
                  <div className="lamp-base-shine"></div>
                </div>
              </motion.div>
              <div className="lamp-shadow"></div>
              <div className="light-rays">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="light-ray" style={{ '--i': i }}></div>
                ))}
              </div>
            </div>

            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              Create Your <span className="gradient-text">Perfect</span> Lamp
            </motion.h3>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              Join thousands of customers designing their dream lighting
            </motion.p>

            {/* Feature Pills */}
            <motion.div
              className="features-grid"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="feature-pill"
                  whileHover={{ scale: 1.05, y: -3 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <span>{feature.text}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Stats */}
            <motion.div
              className="login-stats"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
            >
              <div className="stat">
                <span className="stat-number">10K+</span>
                <span className="stat-label">Happy Customers</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat">
                <span className="stat-number">500+</span>
                <span className="stat-label">Unique Designs</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat">
                <span className="stat-number">4.9★</span>
                <span className="stat-label">Customer Rating</span>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
